/**
 * Match de waitlist (proto agenda-ux-v1 vista 6): primer hueco libre que
 * entra entero para una entrada — misma regla que try_offer del backend:
 * agenda fija o cualquiera + solape con la ventana preferida del cliente.
 * Solo escanea el rango ya cargado (appointments/timeOffs en memoria).
 */
import { DAY_BOARD_SLOT_MINUTES } from './dayBoardLayout';
import { moveTargetMinutesForResource } from './slotOptions';
import type { AppointmentResource } from './types';

export type WaitlistMatchInput = {
  resourceId: string | null;
  preferredFrom: string | null;
  preferredTo: string | null;
};

export type WaitlistEntryMatch = {
  resourceId: string;
  startsAt: Date;
  endsAt: Date;
};

export function waitlistEntryMatch(args: {
  entry: WaitlistMatchInput;
  resources: AppointmentResource[];
  appointments: Array<{
    id: string;
    resourceId: string;
    startsAt: string;
    endsAt: string;
    status: string;
  }>;
  timeOffs: Array<{ resourceId: string; startsAt: string; endsAt: string }>;
  durationMinutes: number;
  fromDay: Date;
  /** Días a escanear desde fromDay (inclusive). 1 = solo ese día. */
  daysAhead: number;
  /** Inyectable en tests — slots ya pasados no son ofrecibles. */
  nowMs?: number;
}): WaitlistEntryMatch | null {
  const { entry } = args;
  const duration = args.durationMinutes > 0 ? args.durationMinutes : DAY_BOARD_SLOT_MINUTES;
  const candidates = entry.resourceId
    ? args.resources.filter((r) => r.id === entry.resourceId)
    : args.resources;
  if (candidates.length === 0 || args.daysAhead <= 0) return null;

  const prefFromMs = entry.preferredFrom ? new Date(entry.preferredFrom).getTime() : null;
  const prefToMs = entry.preferredTo ? new Date(entry.preferredTo).getTime() : null;
  const nowMs = args.nowMs ?? Date.now();

  for (let i = 0; i < args.daysAhead; i++) {
    const day = new Date(
      args.fromDay.getFullYear(),
      args.fromDay.getMonth(),
      args.fromDay.getDate() + i,
    );
    let best: WaitlistEntryMatch | null = null;
    for (const r of candidates) {
      const targets = moveTargetMinutesForResource({
        dayOfWeek: day.getDay(),
        resource: r,
        day,
        timeOffs: (args.timeOffs ?? []).filter((t) => t.resourceId === r.id),
        appointments: args.appointments,
        durationMinutes: duration,
      });
      for (const m of targets) {
        const startsAt = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, m, 0, 0);
        const endsAt = new Date(startsAt.getTime() + duration * 60_000);
        if (startsAt.getTime() <= nowMs) continue;
        // Misma regla de solape que try_offer (preferred_from/to inclusive-exclusive).
        if (prefFromMs != null && endsAt.getTime() <= prefFromMs) continue;
        if (prefToMs != null && startsAt.getTime() >= prefToMs) continue;
        if (!best || startsAt < best.startsAt) {
          best = { resourceId: r.id, startsAt, endsAt };
        }
      }
    }
    // El día más temprano gana — ya barrimos todas las agendas de ese día.
    if (best) return best;
  }
  return null;
}
